/**
 * 权限点字典 —— 静态常量,不是用户可创建的数据。
 *
 * 启动时 upsert 到 permission 表。定义在代码里的好处:重构时改代码就能
 * 发现所有引用点,而不是靠字符串在数据库里飘着。
 *
 * V1 只 seed 两个角色(ADMIN / DRIVER),但**权限点体系全量实现** ——
 * 后期加角色要重新审计每个接口,漏掉的就是越权漏洞。见 docs/13-v1-scope.md §13.4。
 */
export const P = {
  // 订单
  ORDER_READ: 'order:read',
  ORDER_CREATE: 'order:create',
  ORDER_UPDATE: 'order:update',
  ORDER_CANCEL: 'order:cancel',
  ORDER_ASSIGN: 'order:assign',
  ORDER_TRANSFER: 'order:transfer',
  ORDER_UNASSIGN: 'order:unassign',
  ORDER_ACCEPT: 'order:accept',
  ORDER_GRAB: 'order:grab',
  ORDER_PICKUP: 'order:pickup',
  ORDER_DELIVER: 'order:deliver',
  ORDER_FAIL: 'order:fail',
  /**
   * 没有照片也把取货 / 送达记上。
   *
   * ★ 单独一个权限点,而不是跟着 order:pickup 走。
   *
   *   商家配的「必须拍照」是他要的凭证,后台随手绕过去的话
   *   这个配置就等于人人可绕 —— 那它不如不存在。
   *   但司机手机没电、app 装不上的时候,那张单确实需要有人推动,
   *   否则它连同后面的送达、结算、对账一起卡死。
   *
   *   所以:能绕,但要单独授权、必须填理由,而且时间轴上写明
   *   「无照片凭证」—— 事后看得出这一单的送达是谁凭什么记的。
   */
  ORDER_OVERRIDE_PROOF: 'order:override_proof',
  ORDER_EXPORT: 'order:export',
  ORDER_VIEW_PII: 'order:view_pii',
  ORDER_VIEW_PRICE: 'order:view_price',
  ORDER_VIEW_PAYOUT: 'order:view_payout',
  ORDER_REPRICE: 'order:reprice',

  /*
   * 数据分析。
   *
   * ★ 单独一个权限点,不复用 order:read。
   *   报表把**全租户**的数据聚合成一屏:各区单量、各司机人效、客单价。
   *   而 order:read 是按数据范围过滤的 —— 一个只看得到自己片区的调度员
   *   拿到 order:read 是对的,让他顺带看到全公司的经营数字不是。
   */
  ANALYTICS_READ: 'analytics:read',

  // 区域
  ZONE_READ: 'zone:read',
  ZONE_MANAGE: 'zone:manage',
  ZONE_KEYWORD_MANAGE: 'zone:keyword_manage',

  // 计价
  PRICING_READ: 'pricing:read',
  PRICING_MANAGE: 'pricing:manage',

  // 取货点
  PICKUP_POINT_READ: 'pickup_point:read',
  PICKUP_POINT_MANAGE: 'pickup_point:manage',

  // 收件人通讯录
  CONTACT_READ: 'contact:read',
  CONTACT_MANAGE: 'contact:manage',

  // 商家
  CUSTOMER_READ: 'customer:read',
  CUSTOMER_MANAGE: 'customer:manage',

  // 司机
  DRIVER_READ: 'driver:read',
  DRIVER_MANAGE: 'driver:manage',
  DRIVER_VERIFY: 'driver:verify',
  DRIVER_POLICY_MANAGE: 'driver:policy_manage',
  DRIVER_LOCATION_VIEW: 'driver:location_view',
  SHIFT_READ: 'shift:read',
  SHIFT_MANAGE: 'shift:manage',

  // 订单池
  POOL_CONFIG_MANAGE: 'pool:config_manage',

  // 导入
  IMPORT_CREATE: 'import:create',
  IMPORT_CONFIRM: 'import:confirm',
  IMPORT_ROLLBACK: 'import:rollback',

  // 三方集成
  INTEGRATION_READ: 'integration:read',
  INTEGRATION_MANAGE: 'integration:manage',
  INTEGRATION_REPLAY: 'integration:replay',

  // 结算
  SETTLEMENT_READ: 'settlement:read',
  SETTLEMENT_MANAGE: 'settlement:manage',
  SETTLEMENT_PAY: 'settlement:pay',

  // 系统
  USER_READ: 'user:read',
  USER_MANAGE: 'user:manage',
  ROLE_MANAGE: 'role:manage',
  AUDIT_READ: 'audit:read',
  SETTING_MANAGE: 'setting:manage',
  TENANT_MANAGE: 'tenant:manage',
} as const;

export type PermissionCode = (typeof P)[keyof typeof P];

export const ALL_PERMISSIONS = Object.values(P) as PermissionCode[];

/** 危险权限:授予时二次确认 + 高亮,使用时写审计 */
export const DANGEROUS_PERMISSIONS: readonly PermissionCode[] = [
  // 一个按钮就能把整本客户名册(姓名/电话/地址)带出去,
  // 是系统里最大的一次性 PII 外流
  P.ORDER_EXPORT,
  P.PRICING_MANAGE,
  P.IMPORT_ROLLBACK,
  P.INTEGRATION_MANAGE,
  P.SETTLEMENT_PAY,
  P.USER_MANAGE,
  P.ROLE_MANAGE,
  P.DRIVER_POLICY_MANAGE,
  P.TENANT_MANAGE,
];

/** 数据范围:决定能看到哪些数据,见 docs/07-rbac-security.md §7.3 */
export const DATA_SCOPES = ['ALL', 'ZONE', 'CUSTOMER', 'SELF'] as const;
export type DataScope = (typeof DATA_SCOPES)[number];

export interface RoleSeed {
  code: string;
  name: string;
  dataScope: DataScope;
  permissions: readonly PermissionCode[];
}

const except = (...excluded: PermissionCode[]): PermissionCode[] =>
  ALL_PERMISSIONS.filter((p) => !excluded.includes(p));

/**
 * 内置角色。V1 只用前两个,其余保留供演进时启用。
 * is_system = true,租户可复制但不可修改本体 —— 这样升级时新增的权限点
 * 能安全并入,不会覆盖客户的自定义配置。
 */
export const SYSTEM_ROLES: readonly RoleSeed[] = [
  {
    code: 'TENANT_ADMIN',
    name: '管理员',
    dataScope: 'ALL',
    permissions: except(P.TENANT_MANAGE, P.SETTLEMENT_PAY),
  },
  {
    // 三种用工类型共用这一个角色,差异全部在 driver_policy 里
    code: 'DRIVER',
    name: '司机',
    dataScope: 'SELF',
    permissions: [
      P.ORDER_READ,
      P.ORDER_ACCEPT,
      P.ORDER_GRAB,
      P.ORDER_PICKUP,
      P.ORDER_DELIVER,
      P.ORDER_FAIL,
      P.ORDER_VIEW_PII,
      P.ORDER_VIEW_PAYOUT,
      P.ZONE_READ,
      P.PICKUP_POINT_READ,
      P.SHIFT_READ,
      P.SETTLEMENT_READ,
    ],
  },
  {
    code: 'DISPATCHER',
    name: '调度员',
    dataScope: 'ZONE',
    permissions: [
      P.ORDER_READ,
      P.ORDER_CREATE,
      P.ORDER_UPDATE,
      P.ORDER_CANCEL,
      P.ORDER_ASSIGN,
      /*
       * ★ 代司机接单 —— 派下去而司机没点确认的单,调度要能推得动。
       *   没有它的话,这种单在后台只有转派/撤回/取消三条路,
       *   而货常常已经拉走了(见 transitions.ts 里 ACCEPT 那段)。
       */
      P.ORDER_ACCEPT,
      P.ORDER_TRANSFER,
      P.ORDER_UNASSIGN,
      P.ORDER_DELIVER,
      P.ORDER_FAIL,
      P.ORDER_VIEW_PII,
      P.ORDER_VIEW_PAYOUT,
      P.ORDER_EXPORT,
      P.ANALYTICS_READ,
      P.ZONE_READ,
      P.ZONE_KEYWORD_MANAGE,
      P.PICKUP_POINT_READ,
      P.PICKUP_POINT_MANAGE,
      P.CUSTOMER_READ,
      P.CONTACT_READ,
      P.CONTACT_MANAGE,
      P.DRIVER_READ,
      P.DRIVER_LOCATION_VIEW,
      P.SHIFT_READ,
      P.SHIFT_MANAGE,
      P.IMPORT_CREATE,
      P.IMPORT_CONFIRM,
    ],
  },
  {
    code: 'FINANCE',
    name: '财务',
    dataScope: 'ALL',
    permissions: [
      P.ORDER_READ,
      P.ORDER_VIEW_PRICE,
      P.ORDER_VIEW_PAYOUT,
      P.ORDER_REPRICE,
      P.ORDER_EXPORT,
      P.PRICING_READ,
      P.CUSTOMER_READ,
      P.DRIVER_READ,
      P.SETTLEMENT_READ,
      P.SETTLEMENT_MANAGE,
      P.SETTLEMENT_PAY,
      P.AUDIT_READ,
    ],
  },
  {
    code: 'CS',
    name: '客服',
    dataScope: 'ALL',
    permissions: [
      P.ORDER_READ,
      P.ORDER_CREATE,
      P.ORDER_UPDATE,
      P.ORDER_CANCEL,
      P.ORDER_VIEW_PII,
      P.ORDER_VIEW_PRICE,
      P.ZONE_READ,
      P.PRICING_READ,
      P.CUSTOMER_READ,
      P.PICKUP_POINT_READ,
      P.DRIVER_READ,
    ],
  },
];

/** V1 启用的角色 */
export const V1_ROLE_CODES = ['TENANT_ADMIN', 'DRIVER'] as const;

/**
 * 内置角色 → 它该有的权限。**以代码为准**。
 *
 * ★ 为什么不直接读库里的 `role_permission`:
 *   那张表是 seed 写进去的一份**快照**。加一个新权限点之后,
 *   在下一次 seed 跑起来之前,管理员是没有这一项的 ——
 *   而管理员本来就该「按定义拥有全部权限」,
 *   不是「碰巧被分配了当时的那一批」。
 *
 *   真实发生过:加了 `analytics:read` 之后,线上管理员菜单里
 *   看不到「数据分析」,接口一律 403,而权限点明明在权限表里躺着。
 *   排查这件事要先知道「内置角色的权限只有 seed 会同步」——
 *   这属于没人应该需要知道的知识。
 *
 * ★ 库里那份仍然写、仍然留着:界面上要显示这个角色有哪些权限,
 *   而且自定义角色本来就只有那一份。这里只是让**内置角色**的判定
 *   不依赖它 —— 代码是源头,库里那份是它的投影。
 */
export const SYSTEM_ROLE_PERMISSIONS: ReadonlyMap<string, readonly PermissionCode[]> = new Map(
  SYSTEM_ROLES.map((r) => [r.code, r.permissions]),
);
